import { Injectable, OnDestroy } from '@angular/core';
import { interval, Subject } from 'rxjs';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, takeUntil } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UpdateCoordinator implements OnDestroy {

  private destroy$ = new Subject<void>();
  private readonly channel?: BroadcastChannel;
  private static readonly CH_NAME = 'sw-update-bus';
  private static readonly LS_KEY = 'sw-reload-now';
  private waitingForUpdateActivation = false;

  constructor(private swUpdate: SwUpdate) {
    // Listen for reload messages from other tabs
    if ('BroadcastChannel' in window) {
      this.channel = new BroadcastChannel(UpdateCoordinator.CH_NAME);
      this.channel.onmessage = (e) => {
        if (e?.data?.type === 'RELOAD_NOW') this.reloadNow();
      };
    } else {
      (window as any).addEventListener('storage', (e: StorageEvent) => {
        if (e.key === UpdateCoordinator.LS_KEY) this.reloadNow();
      });
    }

    navigator.serviceWorker?.addEventListener('controllerchange', () => {
      if (!this.waitingForUpdateActivation) {
        console.log('Ignoring controllerchange without update activation');
        return;
      }

      setTimeout(() => this.reloadNow(), 50);
    });

    // React when a new version is ready
    this.swUpdate.versionUpdates.pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'),
        takeUntil(this.destroy$),
      ).subscribe(async () => {
        try {
          this.waitingForUpdateActivation = true;

          // Activate the new SW immediately for this origin
          await this.swUpdate.activateUpdate();

          // Tell all other tabs to reload
          this.notifyAllTabsToReload();

          // Reload this tab
          this.reloadNow();
        } catch (err) {
          console.error('Failed to activate update:', err);
        }
      });


    // periodic checks (app stable + every 5 min)
    const appIsStable$ = (window as any).ngRefStable$  // replace with your appIsStable$ if you expose it
      ?? Promise.resolve(true);                        // or wire from ApplicationRef.isStable

    Promise.resolve(appIsStable$).then(() => {
      const every5m$ = interval(300000);

      // first tick right away, then every 5 minutes
      every5m$.subscribe(() => this.tryCheckForUpdate());

      this.tryCheckForUpdate();
    });
  }


  private notifyAllTabsToReload() {
    if (this.channel) {
      this.channel.postMessage({ type: 'RELOAD_NOW' });
    } else {
      // Triggers a 'storage' event in other tabs
      localStorage.setItem(UpdateCoordinator.LS_KEY, String(Date.now()));
    }
  }

  private reloadNow() {
    // No prompt: just hard reload
    window.location.reload();
  }


  private async tryCheckForUpdate() {
    try {
      const found = await this.swUpdate.checkForUpdate();

      if (found) {
        // The VERSION_READY event will fire shortly after install completes.
        console.log('Update found; waiting for VERSION_READY...');
      } else {
        console.log('No update found.');
      }
    } catch (err) {
      console.error('SW update check failed:', err);
    }
  }

  ngOnDestroy() {
    this.destroy$.next(); this.destroy$.complete();
    this.channel?.close();
  }
}
